import { PaginationReturn } from '@common/types/service';
import type { Notification, NotificationSettings } from '@notifications/types';
import { api } from '../../../services/api';

const isNotification = (value: unknown): value is Notification => {
    return typeof value === 'object' && value !== null && 'id' in value;
};

const notificationsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getNotifications: builder.query<Notification[], { ordering?: string; is_read?: boolean }>({
            query: (params) => ({
                url: 'notifications/',
                params,
            }),
            providesTags: (result) =>
                result
                    ? [...result.map(({ id }) => ({ type: 'Notification', id })), { type: 'Notification', id: 'LIST' }]
                    : [{ type: 'Notification', id: 'LIST' }],
        }),
        getPaginatedNotifications: builder.query<
            PaginationReturn<Notification>,
            { page?: number; page_size?: number; ordering?: string; is_read?: boolean }
        >({
            query: (params) => ({
                url: 'notifications/',
                params,
            }),
            providesTags: (result, _err) =>
                result && result.results
                    ? [
                          ...result.results.map(({ id }) => ({ type: 'Notification', id })),
                          { type: 'Notification', id: 'LIST' },
                      ]
                    : [{ type: 'Notification', id: 'LIST' }],
        }),
        markNotificationAsRead: builder.mutation<Notification, { notification_id: Notification['id'] }>({
            query: ({ notification_id }) => ({
                url: `notifications/${notification_id}/mark-read/`,
                method: 'POST',
            }),
            invalidatesTags: (result, _error, { notification_id }) =>
                result
                    ? [
                          { type: 'Notification', id: notification_id },
                          { type: 'Notification', id: 'LIST' },
                      ]
                    : [],
        }),
        markAllNotificationsAsRead: builder.mutation<void, void>({
            query: () => ({
                url: 'notifications/mark-all-read/',
                method: 'POST',
            }),
            invalidatesTags: (res) => (res ? [{ type: 'Notification', id: 'LIST' }] : []),
        }),
        patchNotificationSettings: builder.mutation<void, NotificationSettings>({
            query: (settings) => ({
                url: `notifications/settings/`,
                method: 'PATCH',
                body: settings,
            }),
            invalidatesTags: (result) => (result ? [{ type: 'Notification', id: 'LIST' }] : []),
        }),
        getNotificationSettings: builder.query<NotificationSettings, void>({
            query: () => ({
                url: 'notifications/settings/',
            }),
            providesTags: (result) =>
                result ? [{ type: 'Notification', id: 'SETTINGS' }] : [{ type: 'Notification', id: 'SETTINGS' }],
        }),
        getNotificationStream: builder.query<Notification[], void>({
            // queryFn bypasses the global fetchBaseQuery completely
            queryFn: () => ({ data: [] }),
            keepUnusedDataFor: 0,
            async onCacheEntryAdded(_, { updateCachedData, cacheDataLoaded, cacheEntryRemoved }) {
                const SSE_URL = '/api-professional/stream/notifications/';
                const MAX_RETRIES = 5;
                const RETRY_DELAY_MS = 5_000;

                const pendingNotifications: Notification[] = [];
                let cacheReady = false;
                let stopped = false;
                let currentSource: EventSource | null = null;
                let retryTimeout: ReturnType<typeof setTimeout> | null = null;

                const pushNotification = (notification: Notification) => {
                    if (!cacheReady) {
                        pendingNotifications.push(notification);
                        return;
                    }
                    updateCachedData((draft) => {
                        draft.unshift(notification);
                    });
                };

                const handleNotificationEvent = (event: MessageEvent) => {
                    try {
                        const newNotification: Notification = JSON.parse(event.data);
                        pushNotification(newNotification);
                    } catch (parseError) {
                        console.error('Failed to parse incoming notification stream data:', parseError, event.data);
                    }
                };

                const connect = (retriesLeft: number) => {
                    if (stopped) return;

                    const source = new EventSource(SSE_URL);
                    currentSource = source;

                    source.addEventListener('notification', handleNotificationEvent);
                    source.addEventListener('message', handleNotificationEvent);

                    source.onerror = () => {
                        source.removeEventListener('notification', handleNotificationEvent);
                        source.removeEventListener('message', handleNotificationEvent);
                        source.close();
                        currentSource = null;

                        if (stopped) return;

                        if (retriesLeft > 0) {
                            console.warn(
                                `Notification SSE connection error. Retrying in ${
                                    RETRY_DELAY_MS / 1000
                                }s... (${retriesLeft} attempt${retriesLeft !== 1 ? 's' : ''} left)`
                            );
                            retryTimeout = setTimeout(() => connect(retriesLeft - 1), RETRY_DELAY_MS);
                        } else {
                            console.error('Notification SSE connection failed after maximum retries. Giving up.');
                        }
                    };
                };

                connect(MAX_RETRIES);

                try {
                    await cacheDataLoaded;
                    cacheReady = true;

                    if (pendingNotifications.length > 0) {
                        updateCachedData((draft) => {
                            draft.unshift(...pendingNotifications);
                        });
                        pendingNotifications.length = 0;
                    }
                } catch {}

                await cacheEntryRemoved;

                stopped = true;
                if (retryTimeout !== null) clearTimeout(retryTimeout);
                const sourceToClose = currentSource as EventSource | null;
                currentSource = null;
                if (sourceToClose) {
                    sourceToClose.removeEventListener('notification', handleNotificationEvent);
                    sourceToClose.removeEventListener('message', handleNotificationEvent);
                    sourceToClose.close();
                }
            },
        }),
    }),
});

export const {
    useGetNotificationsQuery,
    useGetNotificationStreamQuery,
    useMarkNotificationAsReadMutation,
    useMarkAllNotificationsAsReadMutation,
    usePatchNotificationSettingsMutation,
    useGetPaginatedNotificationsQuery,
    useLazyGetPaginatedNotificationsQuery,
    useGetNotificationSettingsQuery,
} = notificationsApi;
